import { Link } from "react-router-dom";
import { useEffect, useRef, useState } from "react";

type Product = {
  id: string;
  category?: string;
  categories?: string[];
  name: string;
  description?: string;
  images?: string[];
  main_image?: string;
  visible?: boolean;
  featured?: boolean;
};

const getProductCategories = (product: Product) => {
  const categories =
    product.categories?.filter(Boolean) ?? [];

  if (categories.length > 0) {
    return categories;
  }

  return product.category ? [product.category] : [];
};

const formatProductCategories = (product: Product) =>
  getProductCategories(product).join(", ");

function ProductCard({
  product,
}: {
  product: Product;
}) {
  const [currentIndex, setCurrentIndex] =
    useState(0);

  const allImages = (() => {
    const imgs =
      product.images?.filter(
        (img) => img.trim() !== ""
      ) ?? [];

    if (
      product.main_image &&
      !imgs.includes(product.main_image)
    ) {
      return [
        product.main_image,
        ...imgs,
      ];
    }

    if (product.main_image) {
      return [
        product.main_image,
        ...imgs.filter(
          (img) =>
            img !== product.main_image
        ),
      ];
    }

    return imgs.length > 0
      ? imgs
      : [
          "https://placehold.co/600x400",
        ];
  })();

  const total = allImages.length;

  const prev = (
    e: React.MouseEvent
  ) => {
    e.preventDefault();

    setCurrentIndex(
      (i) => (i - 1 + total) % total
    );
  };

  const next = (
    e: React.MouseEvent
  ) => {
    e.preventDefault();

    setCurrentIndex(
      (i) => (i + 1) % total
    );
  };

  return (
    <div className="min-w-[320px] max-w-[320px] bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-[#3FA7D6] transition-all group flex-shrink-0">
      <div className="relative w-full h-52 overflow-hidden bg-gray-50">
        <img
          src={allImages[currentIndex]}
          alt={`${product.name}`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />

        <span className="absolute top-3 left-3 bg-[#1B1F5E] text-white font-bold text-xs px-2.5 py-1 rounded z-10">
          {formatProductCategories(product) ||
            "Produto"}
        </span>

        {total > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-2 top-1/2 -translate-y-1/2 z-10 bg-black/40 hover:bg-black/65 text-white rounded-full w-8 h-8 flex items-center justify-center"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>

            <button
              onClick={next}
              className="absolute right-2 top-1/2 -translate-y-1/2 z-10 bg-black/40 hover:bg-black/65 text-white rounded-full w-8 h-8 flex items-center justify-center"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </>
        )}
      </div>

      <div className="p-5">
        <h3 className="font-rajdhani font-bold text-[#1B1F5E] text-lg leading-snug mb-2">
          {product.name}
        </h3>

        <p className="text-[#666] text-sm leading-relaxed mb-4 line-clamp-4">
          {product.description}
        </p>

        <Link
          to="/produtos"
          className="inline-flex items-center gap-1.5 text-[#3FA7D6] font-bold text-xs uppercase tracking-wider hover:gap-2.5 transition-all"
        >
          ACESSAR{" "}
          <i className="ri-arrow-right-double-line"></i>
        </Link>
      </div>
    </div>
  );
}

export default function NTProductsSection() {
  const [products, setProducts] =
    useState<Product[]>([]);

  const [loading, setLoading] =
    useState(true);

  const scrollRef =
    useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch("/api/products.php")
      .then((res) => res.json())
      .then((data) => {
        const visibleProducts =
          data.filter(
            (p: Product) =>
              p.visible !== false
          );

        setProducts(visibleProducts);

        setLoading(false);
      })
      .catch((err) => {
        console.error(err);

        setLoading(false);
      });
  }, []);

  const scrollLeft = () => {
    if (!scrollRef.current) return;

    scrollRef.current.scrollBy({
      left: -340,
      behavior: "smooth",
    });
  };

  const scrollRight = () => {
    if (!scrollRef.current) return;

    scrollRef.current.scrollBy({
      left: 340,
      behavior: "smooth",
    });
  };

  return (
    <section className="bg-[#f5f5f5] py-20">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <p className="text-[#3FA7D6] font-bold text-sm uppercase tracking-widest mb-1">
              Linha de Produtos
            </p>

            <h2 className="font-rajdhani font-bold text-[#1B1F5E] text-4xl md:text-5xl">
              Produtos em Destaque
            </h2>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={scrollLeft}
              className="w-11 h-11 rounded-full border border-gray-300 hover:bg-[#1B1F5E] hover:text-white transition-all flex items-center justify-center"
            >
              <i className="ri-arrow-left-line"></i>
            </button>

            <button
              onClick={scrollRight}
              className="w-11 h-11 rounded-full border border-gray-300 hover:bg-[#1B1F5E] hover:text-white transition-all flex items-center justify-center"
            >
              <i className="ri-arrow-right-line"></i>
            </button>

            <Link
              to="/produtos"
              className="ml-3 inline-flex items-center gap-2 text-[#1B1F5E] font-bold text-sm hover:gap-3 transition-all whitespace-nowrap"
            >
              Ver Todos{" "}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="flex gap-5 overflow-hidden">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="min-w-[320px] h-[420px] bg-white border border-gray-200 rounded-lg overflow-hidden animate-pulse"
              >
                <div className="w-full h-52 bg-gray-100" />

                <div className="p-5 space-y-3">
                  <div className="h-4 bg-gray-100 rounded w-3/4" />

                  <div className="h-3 bg-gray-100 rounded w-full" />

                  <div className="h-3 bg-gray-100 rounded w-5/6" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div
            ref={scrollRef}
            className="flex gap-5 overflow-x-auto scroll-smooth no-scrollbar pb-2"
          >
            {products.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
